import { FC, useCallback, useEffect, useState } from "react"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CmsMediaItem } from "services/cms-seo/type"

import { formatBytes } from "./helpers/format"
import { ghostButtonClass, inputClass, modalBoxClass} from "./CmsUi"

const PAGE_SIZE = 18

interface MediaPickerModalProps {
  open: boolean
  onClose: () => void
  onPick: (item: CmsMediaItem) => void
}

export const MediaPickerModal: FC<MediaPickerModalProps> = ({ open, onClose, onPick }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [items, setItems] = useState<CmsMediaItem[]>([])
  const [search, setSearch] = useState("")
  const [loading, setLoading] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const data = await cmsSeoService.listMedia({ page: 1, pageSize: PAGE_SIZE, q: search.trim() || undefined })
    setItems(data.items)
    setLoading(false)
  }, [search])

  useEffect(() => {
    if (open) load()
  }, [open, load])

  return (
    <Modal open={open} handler={onClose}>
      <div className={`${modalBoxClass} max-w-3xl`}>
        <h3 className="mb-4 text-lg font-semibold">{t("cmsSeo.chooseFromLibrary", { defaultValue: "Choose from library" })}</h3>

        <input
          className={`${inputClass} mb-4`}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("cmsSeo.filter.searchMedia", { defaultValue: "Search media…" }) as string}
        />

        {loading ? (
          <p className="py-8 text-center text-sm">{t("common.loading", { defaultValue: "Loading..." })}</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm opacity-70">{t("cmsSeo.noMedia", { defaultValue: "The library is empty." })}</p>
        ) : (
          <div className="grid max-h-[420px] grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3 overflow-y-auto">
            {items.map((item) => (
              <button
                key={item.id}
                type="button"
                className="overflow-hidden rounded-lg border border-black/[0.08] text-left hover:border-purple-500 dark:border-white/[0.1]"
                onClick={() => {
                  onPick(item)
                  onClose()
                }}
              >
                <div className="flex aspect-[1.6/1] items-center justify-center bg-black/[0.04] dark:bg-white/[0.04]">
                  <img src={item.url} alt={item.alt} className="max-h-full max-w-full object-contain" loading="lazy" />
                </div>
                <div className="p-2">
                  <div className="truncate text-[11px] font-medium">{item.filename}</div>
                  <div className="text-[10px] text-gray-500 dark:text-gray-400">
                    {item.width}×{item.height} · {formatBytes(item.size_bytes)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="modal-action flex-wrap">
          <button type="button" className={ghostButtonClass} onClick={onClose}>
            {t("common.close", { defaultValue: "Close" })}
          </button>
        </div>
      </div>
    </Modal>
  )
}
